import { useState, useMemo } from 'react';
import { useAsyncEffect } from 'hooks';
import { getIsHandleTaken } from '../resources/Auth/services';
import { detectToxicHandle } from './detectToxicHandle';
import { detectContactInfo } from './detectContactInfo';
import HandleBlocklistResource from '../resources/HandleBlocklist';
import sleep from './sleep';

export type HandleValidationError = 'taken' | 'toxic' | 'contactInfo' | 'blocked' | null;

export const handleValidationErrorCopyKeys = {
  taken: 'shootThatOneSAlreadyTaken',
  contactInfo: 'handleIsContactInfo',
  toxic: 'toxicHandle',
  blocked: 'handleBlocked',
} as const;

let getIsHandleBlocked = (handle: string, blocklist: string[]): boolean => {
  let normalized = handle.toLowerCase().trim();
  return blocklist.some(blocked => normalized === blocked.toLowerCase());
};

let normalizeHandle = (handle: string): string => {
  return handle.toLowerCase().trim().slice(0, 30);
};

export function useHandleValidation(currentHandle: string) {
  const [handleText, setHandleText] = useState(currentHandle || '');
  const [isHandleTaken, setIsHandleTaken] = useState<boolean | undefined>(undefined);
  const [blocklist] = HandleBlocklistResource.useLoadable();

  const normalizedHandle = useMemo(() => normalizeHandle(handleText), [handleText]);
  const normalizedCurrentHandle = useMemo(() => normalizeHandle(currentHandle), [currentHandle]);

  const isHandleToxic = useMemo(() => detectToxicHandle(normalizedHandle), [normalizedHandle]);
  const isHandleContactInfo = useMemo(() => detectContactInfo(normalizedHandle), [normalizedHandle]);
  const isHandleBlocked = useMemo(
    () => (normalizedCurrentHandle === normalizedHandle ? false : getIsHandleBlocked(normalizedHandle, blocklist || [])),
    [normalizedHandle, normalizedCurrentHandle, blocklist],
  );

  useAsyncEffect(
    async (getIsCurrent) => {
      if (normalizedCurrentHandle === normalizedHandle) {
        setIsHandleTaken(false);
        return;
      }
      setIsHandleTaken(undefined);
      await sleep(300);
      if (!getIsCurrent()) return;
      const taken = await getIsHandleTaken(normalizedHandle);
      if (!getIsCurrent()) return;
      setIsHandleTaken(taken);
    },
    [normalizedHandle, normalizedCurrentHandle],
  );

  const validationError: HandleValidationError =
    isHandleTaken === true
      ? 'taken'
      : isHandleBlocked === true
        ? 'blocked'
        : isHandleContactInfo
          ? 'contactInfo'
          : isHandleToxic
            ? 'toxic'
            : null;

  const canSave = Boolean(normalizedHandle) && isHandleTaken === false && isHandleBlocked === false && !isHandleContactInfo;

  return {
    handleText,
    setHandleText,
    normalizedHandle,
    validationError,
    canSave,
  };
}
